import { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import Button from "@mui/material/Button";
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import MessageModal from 'app/components/MessageModal';

import classes from 'app/components/Modal/classes.module.scss';

import {
  getPricingTiers as getPricingTiersSvc,
  createPricingTier as createPricingTierSvc,
  updatePricingTier as updatePricingTierSvc,
  deletePricingTier as deletePricingTierSvc
} from 'app/services/jobConfigs';

import {
  formatCurrency,
  objectsAreSame
} from 'app/utils/valueFormatters';

const VolumePricingForm = (props) => {
  const { isOpen, onClose, selectedJobConfig, selectedPriceModel } = props;
  const [itemsChanged, setItemsChanged] = useState(false);
  const [data, setData] = useState([]);
  const [origData, setOrigData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const modalStyle = {};

  modalStyle.justifyContent = 'end';
  modalStyle.marginRight = '1.5rem';

  let boxClass = classes.paper,
    boxStyle = null,
    headerVariant = 'h5';

  useEffect(() => {
    if (!loaded) {
      getPricingTiersSvc(selectedJobConfig?.product_id).then((result) => {
        // Filter out tiers from other pricing model
        result = result.filter((item) => { return item.tier_type === selectedPriceModel });
        result.forEach((item) => {
          item.x = 'X';

          // Handling Percentage based discounts
          if (item.tier_type === 2) {
            item.price *= 100;
          }
        })
        setData(result);
        setOrigData(result);
        setLoaded(true);
      })
    }
  }, [loaded])

  useEffect(() => {
    let isChanged = false;

    if (!objectsAreSame(origData, data)) {
      isChanged = true;
    }

    setItemsChanged(isChanged);
  }, [origData, data, isOpen])

  useEffect(() => {
    setLoaded(false);
  }, [selectedPriceModel])

  const handleDeleteRow = (rowIndex) => {
    const tiers = [...data];
    tiers.splice(rowIndex, 1);
    setData(tiers);
  }

  const onSubmit = async () => {
    let hasError = false;

    for (let item of data) {
      // Handling Percentage based discounts
      if (item.tier_type === 2) {
        item.price /= 100;
      }

      const priceParams = Object.assign({}, item);
      priceParams.price = item.price.toFixed(2);
      if (item.id > 0) {
        await updatePricingTierSvc(selectedJobConfig.product_id, item.id, priceParams).catch((e) => {
            hasError = e.error !== null || e.error !== '';
            setError(e.error);
        })
      } else {
        await createPricingTierSvc(selectedJobConfig.product_id, priceParams).catch((e) => {
            hasError = e.error !== null || e.error !== '';
            setError(e.error);
        })
      }
    }

    if (hasError) {
      return;
    }

    let forDeletes = [];
    let dataIds = data.map(d => { return d.id });
    for (let orig of origData) {
      if (orig.id && !dataIds.includes(orig.id)) {
        forDeletes.push(orig);
      }
    }

    for (let item of forDeletes) {
      await deletePricingTierSvc(selectedJobConfig?.product_id, item.id);
    }

    setLoaded(false);
    onClose();

  }

  const deleteRowBody = (data, row) => {
    return (
      <div>
        <IconButton aria-label="delete" onClick={() => handleDeleteRow(row.rowIndex)}>
          <DeleteIcon />
        </IconButton>
      </div>
    )
  }

  const percentBodyTemplate = (rowData, column) => {
    const jobConfig = selectedJobConfig;
    const price = jobConfig?.product?.price;
    const discount = (rowData[column.field] / 100) * price;
    const total = price - discount;
    return <label>{(rowData[column.field]) + "%"} ({formatCurrency(total, jobConfig?.currency)})</label>;
  }

  const packageBodyTemplate = (rowData, column) => {
    const jobConfig = selectedJobConfig;
    const price = rowData.price;
    const momentPrice = price / rowData.requirement;
    return <label>{formatCurrency(rowData[column.field], jobConfig?.currency)} ({formatCurrency(momentPrice, jobConfig?.currency)})</label>;
  }

  const onRowEditComplete = (e) => {
    let tiers = [...data];
    let { newData, index } = e;

    tiers[index] = newData;
    tiers.forEach((item) => {
      item.tier_type = selectedPriceModel;
    })

    setData(tiers);
  };

  const fieldEditor = (options) => {
    return (
      <div>
        <InputNumber value={options.value} onChange={(e) => options.editorCallback(e.value)} minFractionDigits={2} maxFractionDigits={5} />
      </div>
    )
  }

  const footerBody = () => {
    return <div>
      <Button variant="contained" onClick={handleAddRow}>Add</Button>
    </div>
  }

  const handleAddRow = () => {
    const tiers = [...data];
    tiers.push({ id: new Date().getTime() * -1, price: 0, x: 'X', requirement: 0 });
    setData(tiers);
  }

  return (
    <div>
      <Modal open={isOpen} className={classes.modal} onClose={onClose}>
        <Box className={boxClass} style={boxStyle}>
          <CloseIcon className={classes.closeIcon} onClick={onClose} />

          <Typography variant={headerVariant} className={classes.formTitle}>
            {selectedPriceModel === 2 ? 'Percent Discount' : selectedPriceModel === 3 ? 'Package Price' : 'Fixed Price'}
          </Typography>

          <DataTable value={data} editMode="row" dataKey="id" onRowEditComplete={onRowEditComplete} footer={footerBody} >
            <Column header='Package Price' field='price' body={selectedPriceModel === 2 ? percentBodyTemplate : packageBodyTemplate} editor={(options) => fieldEditor(options)} />
            <Column header='X' field='x' />
            <Column header='After # of moments' field='requirement' editor={(options) => fieldEditor(options)} />
            <Column rowEditor={true} headerStyle={{ width: '10%', minWidth: '8rem' }} bodyStyle={{ textAlign: 'center' }}></Column>
            <Column body={deleteRowBody} ></Column>
          </DataTable>

          <Button className={classes.button} variant="contained" onClick={onClose}>Cancel</Button>
          <Button className={classes.button} variant="contained" disabled={!itemsChanged} onClick={() => onSubmit()}>Save</Button>
        </Box>
    </Modal>
    <MessageModal
      isOpen={error !== null}
      onClose={() => setError(null)}
      title='Package Pricing'
      message={error}
    />
    </div>
  )
}

export default VolumePricingForm;
